import React, { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ExcelJS from "exceljs";
import { Download, Upload, X } from "lucide-react";
import toast from "react-hot-toast";
import { DsButton } from "../../../design-system";
import { categoryService } from "../../../services/api";
import {
  ImportValidationReview,
  buildImportValidationResult,
  downloadValidationStatusWorkbook,
} from "../../Common/import-validation";

/**
 * Bulk import Category Minimum Wages — validate → review → consent import.
 */
export default function CategoryMinimumWagesImportModal({
  darkMode,
  open,
  onClose,
  onImported,
  categories = [],
  branches = [],
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [review, setReview] = useState(null);
  const [pendingRows, setPendingRows] = useState([]);

  const categoryNames = useMemo(
    () =>
      [
        ...new Set(
          (categories || []).map((c) => String(c.name || "").trim()).filter(Boolean)
        ),
      ].sort(),
    [categories]
  );
  const branchNames = useMemo(
    () =>
      [
        ...new Set(
          (branches || []).map((b) => String(b.name || "").trim()).filter(Boolean)
        ),
      ].sort(),
    [branches]
  );
  const categorySet = useMemo(
    () => new Set(categoryNames.map((n) => n.toLowerCase())),
    [categoryNames]
  );
  const branchSet = useMemo(
    () => new Set(branchNames.map((n) => n.toLowerCase())),
    [branchNames]
  );

  if (!open || typeof document === "undefined") return null;

  const resetReview = () => {
    setReview(null);
    setPendingRows([]);
  };

  const downloadTemplate = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Minimum Wages");
    const lists = wb.addWorksheet("Lists");
    lists.getColumn(1).values = ["Category", ...categoryNames];
    lists.getColumn(2).values = ["Branch", ...branchNames, "(All branches)"];
    lists.state = "hidden";

    ws.addRow(["Category", "Branch", "Amount", "Effective Month (YYYY-MM)"]);
    ws.getRow(1).font = { bold: true };
    ws.addRow([
      categoryNames[0] || "",
      branchNames[0] || "(All branches)",
      18000,
      new Date().toISOString().slice(0, 7),
    ]);
    ws.getColumn(1).width = 24;
    ws.getColumn(2).width = 22;
    ws.getColumn(3).width = 14;
    ws.getColumn(4).width = 26;

    for (let r = 2; r <= 201; r += 1) {
      if (categoryNames.length) {
        ws.getCell(`A${r}`).dataValidation = {
          type: "list",
          allowBlank: false,
          formulae: [`Lists!$A$2:$A$${categoryNames.length + 1}`],
          showErrorMessage: true,
          errorTitle: "Category",
          error: "Select a category from the list",
        };
      }
      if (branchNames.length) {
        ws.getCell(`B${r}`).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [`Lists!$B$2:$B$${branchNames.length + 2}`],
          showErrorMessage: true,
          errorTitle: "Branch",
          error: "Select a branch from the list (or All branches)",
        };
      }
    }

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Category_Minimum_Wages_Template.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleValidate = async (selected) => {
    const f = selected || file;
    if (!f) {
      toast.error("Please choose a file first.");
      return;
    }
    setBusy(true);
    resetReview();
    try {
      const buf = await f.arrayBuffer();
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(buf);
      const ws = wb.worksheets[0];
      if (!ws) throw new Error("No worksheet found");

      const validRecords = [];
      const invalidRecords = [];

      ws.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        const categoryName = String(
          row.getCell(1).text || row.getCell(1).value || ""
        ).trim();
        let branchName = String(
          row.getCell(2).text || row.getCell(2).value || ""
        ).trim();
        if (/^\(all branches\)$/i.test(branchName) || !branchName) branchName = "";
        const amount = Number(row.getCell(3).value);
        let effectiveMonth = String(
          row.getCell(4).text || row.getCell(4).value || ""
        ).trim();
        if (/^\d{4}-\d{2}/.test(effectiveMonth)) {
          effectiveMonth = effectiveMonth.slice(0, 7);
        }
        if (!categoryName && !amount) return;

        const payload = {
          categoryName,
          branchName,
          amount,
          effectiveMonth,
        };
        const record = {
          Name: categoryName,
          "Employee Code": categoryName,
          "Employee Name": branchName || "(All branches)",
          ...payload,
        };
        const fieldErrors = {};
        if (!categoryName) fieldErrors.Category = "Category is required";
        else if (!categorySet.has(categoryName.toLowerCase())) {
          fieldErrors.Category = `Unknown category “${categoryName}”`;
        }
        if (
          branchName &&
          !branchSet.has(branchName.toLowerCase())
        ) {
          fieldErrors.Branch = `Unknown branch “${branchName}”`;
        }
        if (!Number.isFinite(amount) || amount < 0) {
          fieldErrors.Amount = "Amount must be a non-negative number";
        }
        if (!/^\d{4}-\d{2}$/.test(effectiveMonth)) {
          fieldErrors["Effective Month"] =
            "Effective Month must be YYYY-MM (e.g. 2026-07)";
        }

        if (Object.keys(fieldErrors).length) {
          invalidRecords.push({
            row: rowNumber,
            record,
            fieldErrors,
            error: Object.values(fieldErrors).join("; "),
          });
        } else {
          validRecords.push({
            __row: rowNumber,
            row: rowNumber,
            record,
            normalizedRecord: payload,
          });
        }
      });

      if (!validRecords.length && !invalidRecords.length) {
        toast.error("No data rows found in the file");
        return;
      }

      const built = buildImportValidationResult({ validRecords, invalidRecords });
      setPendingRows(validRecords.map((v) => v.normalizedRecord));
      setReview(built);
      setFile(f);
      setFileName(f.name);
      toast.success(
        `Validated: ${built.successCount} successful, ${built.failedCount} failed`
      );
    } catch (err) {
      toast.error(err?.message || "Failed to read file");
    } finally {
      setBusy(false);
    }
  };

  const runImport = async ({ ignoreFailed }) => {
    if (!review) return;
    if (!ignoreFailed && review.failedCount > 0) {
      toast.error("Fix failed rows or choose Ignore failed & import.");
      return;
    }
    if (!pendingRows.length) {
      toast.error("No successful rows to import");
      return;
    }
    setBusy(true);
    try {
      const res = await categoryService.bulkImportMinimumWages(pendingRows);
      const errs = res?.data?.errors || [];
      if (errs.length) {
        toast.error(
          `Import finished with ${errs.length} error(s). First: ${errs[0].message}`
        );
      } else {
        toast.success(res?.message || "Import complete");
      }
      onImported?.(res);
      resetReview();
      setFile(null);
      setFileName("");
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!review || !file) {
      toast.error("Validate a file first.");
      return;
    }
    try {
      setBusy(true);
      await downloadValidationStatusWorkbook(
        file,
        review.allRows,
        "Minimum_Wages_Validation_Status.xlsx",
        { sheetName: "Minimum Wages" }
      );
      toast.success("Validation report downloaded");
    } catch (err) {
      toast.error(err?.message || "Failed to download report");
    } finally {
      setBusy(false);
    }
  };

  const panel = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]";

  const showReview = !!review;

  return createPortal(
    <div
      className="fixed inset-0 z-[100060] flex items-center justify-center bg-black/50 p-4"
      onClick={() => !busy && onClose?.()}
      role="presentation"
    >
      <div
        className={`flex max-h-[90vh] w-full flex-col overflow-hidden rounded-xl border p-0 shadow-xl ${panel} ${
          showReview ? "max-w-4xl" : "max-w-lg"
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between gap-2 border-b border-[hsl(var(--ds-border))] px-5 py-4">
          <div>
            <h3 className="text-base font-bold">Import Minimum Wages</h3>
            {!showReview ? (
              <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">
                Use the template (Category + Branch dropdowns). Validate first, then
                consent to import.
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 hover:opacity-70"
            disabled={busy}
          >
            <X size={18} />
          </button>
        </div>

        {showReview ? (
          <div className="flex min-h-0 flex-1 flex-col px-5 py-4">
            <ImportValidationReview
              result={review}
              busy={busy}
              title="Minimum wages validation"
              entityLabel="row"
              onBack={resetReview}
              onDownloadReport={handleDownloadReport}
              onImportAll={() => runImport({ ignoreFailed: false })}
              onImportSuccessfulOnly={() => runImport({ ignoreFailed: true })}
            />
          </div>
        ) : (
          <div className="space-y-4 px-5 py-4">
            <div className="flex flex-wrap gap-2">
              <DsButton type="button" variant="outline" size="sm" onClick={downloadTemplate}>
                <Download size={14} /> Download template
              </DsButton>
              <DsButton
                type="button"
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
              >
                <Upload size={14} /> Choose Excel
              </DsButton>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                disabled={busy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  setFile(f);
                  setFileName(f.name);
                  handleValidate(f);
                }}
              />
            </div>
            {fileName ? (
              <p className="text-[11px] text-[hsl(var(--ds-muted-fg))]">
                Selected: {fileName}
              </p>
            ) : null}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
